import { useCallback, useRef, useEffect } from "react";
import { connectToParent } from "penpal";
import toast from "react-hot-toast";
import { useUserStore } from "../store/userStore";
import { useGuideStore } from "../store/guideStore";
import { useNavigateWithLock } from "./useNavigateWithLock";
import { apiClient } from "../services/api";
import { modeToUrl } from "../lib/navigation-helpers";
import type { Guide, Step, PublishProgress, ClientAppInfo, ParentMethods, AppMode } from "../types";

// Simple connection type for now
interface SimpleConnection {
  promise: Promise<ParentMethods>;
  destroy: () => void;
}

// Global singleton connection
let globalConnection: SimpleConnection | null = null;
let isInitialized = false;

/**
 * Check if the app is running in an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // If we can't access window.top, we're likely in an iframe
  }
};

export const usePenpal = () => {
  const callbacksRef = useRef<{
    clearPublishState: () => void;
    setGuideToPublish: (guide: Guide, steps: Step[]) => void;
    // TODO: remove this logic of current guide and  guide to publish
    setCurrentGuide: (guide: Guide) => void;
    getGuideToPublish: () => Guide | null;
    getCurrentGuide: () => Guide | null;
    navigate: (mode: AppMode, params?: Record<string, any>) => void;
    setClientAppInfo: (info: ClientAppInfo) => void;
    login: (token: string, account: any, properties: any, info: ClientAppInfo) => void;
    updatePublishProgress: (progress: PublishProgress) => void;
    finishPublishing: (success: boolean) => void;
  } | null>(null);

  // Store hooks
  const { setClientAppInfo, login } = useUserStore();
  const {
    setGuideToPublish,
    setCurrentGuide,
    updatePublishProgress,
    getGuideToPublish,
    getCurrentGuide,
    clearPublishState,
    finishPublishing,
  } = useGuideStore();
  const navigate = useNavigateWithLock();

  // Helper function to navigate using mode (for backward compatibility)
  const navigateWithMode = useCallback((mode: AppMode, params?: Record<string, any>) => {
    const url = modeToUrl(mode, params);
    navigate(url);
  }, [navigate]);

  // Update callbacks ref with latest functions
  useEffect(() => {
    callbacksRef.current = {
      clearPublishState,
      setGuideToPublish,
      setCurrentGuide,
      getGuideToPublish,
      getCurrentGuide,
      navigate: navigateWithMode,
      setClientAppInfo,
      login,
      updatePublishProgress,
      finishPublishing,
    };
  });

  // Initialize global connection only once and only if in iframe
  useEffect(() => {
    if (isInitialized) {
      return; // Already initialized
    }

    // Only initialize Penpal if we're in an iframe
    if (!isInIframe()) {
      return; // Not in iframe, skip Penpal initialization
    }
    isInitialized = true;
    globalConnection = connectToParent({
      methods: {
        errorDuringExport: (text: string) => {
          callbacksRef.current?.clearPublishState();
          toast.error(text, {
            position: "top-center",
            duration: 5000,
          });
        },
        dataToPublish: async ({ guide, steps, startPage }: { guide: Guide; steps: Step[], startPage?: string }) => {
          // Only until dahboasrd is ready
          if(startPage == 'dashboard') {
            callbacksRef.current?.navigate("guides");
            return;
          }
          callbacksRef.current?.setGuideToPublish(guide, steps);

          try {
            const response = await apiClient.getGuide(guide.id);
            if (response.ok && response.guide) {
              callbacksRef.current?.setCurrentGuide(response.guide);
              callbacksRef.current?.navigate("edit-guide", { id: guide.id });
            } else {
              callbacksRef.current?.navigate("add-guide");
            }
          } catch (error) {
            console.error("Error checking guide existence:", error);
            callbacksRef.current?.navigate("add-guide");
          }
        },
        handshake: async ({ token, info }: { token: string; info: ClientAppInfo }) => {
          callbacksRef.current?.setClientAppInfo(info);
          try {
            const { me, properties } = await apiClient.handshake(token);
            callbacksRef.current?.login(token, me, properties, info);
            return true;
          } catch (error) {
            console.error("Handshake error:", error);
            return false;
          }
        },
        updateProgress: async (info: PublishProgress, error?: string) => {
          if (error) {
            callbacksRef.current?.clearPublishState();
            return;
          }
          const guideToPublish = callbacksRef.current?.getGuideToPublish();
          callbacksRef.current?.updatePublishProgress(info);
          if (info.success) {
            callbacksRef.current?.finishPublishing(true);

            try {
              // Get updated guide data
              const idFromInfo = info.internalGuideId ? info.internalGuideId : info.guideId ? info.guideId : "";
              const currentGuideId = idFromInfo ? idFromInfo : guideToPublish ? guideToPublish.id : "";
              const response = await apiClient.getGuide(currentGuideId);
              
              if (response.ok && response.guide) {
                callbacksRef.current?.setCurrentGuide(response.guide);
                callbacksRef.current?.navigate("edit-guide", { id: currentGuideId });
              } else {
                callbacksRef.current?.navigate("add-guide");
              }
              toast.success("Guide was successfully published", {
                position: "top-center",
                duration: 3000,
              });
            } catch (error) {
              console.error("Error getting updated guide:", error);
              callbacksRef.current?.navigate("add-guide");
            }
          }
        },
      },
    }) as unknown as SimpleConnection;
  }, []); // Empty dependency array - only run once

  // Helper functions for parent communication
  const openExternalUrl = useCallback(async (url: string) => {
    if (globalConnection) {
      // If we're in an iframe, use the parent's openExternalUrl method
      const parent = await globalConnection.promise;
      parent.openExternalUrl(url);
    } else {
      // If we're in a browser, use window.open for external URLs
      // For mailto links, we can use window.location.href or window.open
      if (url.startsWith('mailto:')) {
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    }
  }, []);

  const publishGuide = useCallback(
    async (options: {
      existingGuideId?: string;
      existingGuideInternalId?: string;
      exportFormat?: string;
      confluenceSpace?: string;
      confluencePageTitle?: string;
    }) => {
      if (globalConnection) {
        const parent = await globalConnection.promise;
        parent.publishGuide(options);
      }
    },
    []
  );

  const cancelExport = useCallback(async () => {
    if (globalConnection) {
      const parent = await globalConnection.promise;
      parent.cancelExport();
    }
  }, []);

  const setSettings = useCallback(async (name: string, value: unknown) => {
    if (globalConnection) {
      const parent = await globalConnection.promise;
      return parent.setSettings(name, value);
    }
  }, []);

  const getSettings = useCallback(async () => {
    if (globalConnection) {
      const parent = await globalConnection.promise;
      return parent.getSettings();
    }
  }, []);

  const getAllLocalGuides = useCallback(async () => {
    if (globalConnection) {
      const parent = await globalConnection.promise;
      return parent.getAllLocalGuides();
    }
  }, []);

  return {
    connection: globalConnection,
    openExternalUrl,
    publishGuide,
    cancelExport,
    setSettings,
    getSettings,
    getAllLocalGuides,
    isConnected: !!globalConnection,
    isInIframe: isInIframe(),
  };
};